import { useMutation, useQuery } from "@apollo/client/react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import {
  CreateIngredientDraftDocument,
  GetIngredientDocument,
  type IngredientFragment,
} from "../../gql/generated/graphqlSchema.tsx";
import { NotFoundWithBack } from "../../components/NotFoundWithBack.tsx";
import { PageLoader } from "../../components/PageLoader.tsx";
import { useSafeParams } from "../../hooks/useSafeParams.ts";
import { IngredientProvider } from "./IngredientContext.tsx";
import { IngredientDetailEdit } from "./edit/IngredientDetailEdit.tsx";
import { IngredientDetailView } from "./view/IngredientDetailView.tsx";
import { RefreshProvider, useUpdateRefreshKey } from "../../context/RefreshContext.tsx";

const paramsSchema = z.object({ id: z.string().min(1) });

type DetailInnerProps = {
  readonly mode: "view" | "edit";
};

const IngredientDetailPageInner = ({ mode }: DetailInnerProps) => {
  const { t } = useTranslation();
  const { id } = useSafeParams(paramsSchema);
  const { data, loading, refetch } = useQuery(GetIngredientDocument, { variables: { id } });
  const [previousData, setPreviousData] = useState(data);
  const [ingredient, setIngredient] = useState<IngredientFragment | null>(null);

  const [createDraft, { loading: creatingDraft }] = useMutation(CreateIngredientDraftDocument);

  if (data !== previousData) {
    setPreviousData(data);
    if (mode === "edit") {
      setIngredient(data?.draftIngredient ?? null);
    } else {
      setIngredient(data?.publishedIngredient ?? null);
    }
  }
  const updateRefreshKey = useUpdateRefreshKey();

  const setIngredientWithRefreshKeyUpdate = useCallback(
    (newIngredient: IngredientFragment | undefined | null) => {
      if (newIngredient) {
        setIngredient(newIngredient);
        updateRefreshKey();
      }
    },
    [updateRefreshKey],
  );

  const handleRefetch = useCallback(() => refetch(), [refetch]);

  useEffect(() => {
    if (mode !== "edit" || loading) return;
    if (data && !data.draftIngredient && data.publishedIngredient && !data.publishedIngredient.deleted) {
      void createDraft({ variables: { id } }).then(() => void refetch());
    }
  }, [mode, loading, data, id, createDraft, refetch]);

  if (loading || creatingDraft) {
    return <PageLoader />;
  }

  if (!ingredient) {
    return <NotFoundWithBack description={t("ingredients.notFound")} />;
  }

  return (
    <IngredientProvider
      ingredient={ingredient}
      refetch={handleRefetch}
      setIngredient={setIngredientWithRefreshKeyUpdate}
    >
      {mode === "edit" && !ingredient.deleted ? (
        <IngredientDetailEdit key={ingredient.versionId} />
      ) : (
        <IngredientDetailView key={ingredient.versionId} />
      )}
    </IngredientProvider>
  );
};

// eslint-disable-next-line react/no-multi-comp
export const IngredientDetailPage = () => (
  <RefreshProvider>
    <IngredientDetailPageInner mode="view" />
  </RefreshProvider>
);

// eslint-disable-next-line react/no-multi-comp
export const IngredientDetailEditPage = () => (
  <RefreshProvider>
    <IngredientDetailPageInner mode="edit" />
  </RefreshProvider>
);
